import qs from 'qs';

import type { operations } from '@luxuryescapes/contract-trip/server';

import { request } from './common';

function tripBasePath() {
  return window.configs.API_HOST + '/api/trips';
}

function getRequest(path: string, options?: { signal?: AbortSignal }) {
  return request(path, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    ...(options?.signal ? { signal: options.signal } : {}),
  });
}

export type ChatOriginSurface = NonNullable<
  operations['getChatTripsListAsAdmin']['parameters']['query']['originSurface']
>;
export interface AdminChatConversationDetails {
  origin?: operations['getChatTripsListAsAdmin']['responses'][200]['content']['application/json']['result']['trips'][number]['origin'];
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface GetChatConversationsResponse {
  status: number;
  result: {
    trips: Array<AdminChatConversationDetails>;
    numberOfTrips: number;
  };
  hasMore?: boolean;
}

export interface GetChatConversationsOptions {
  signal?: AbortSignal;
}

export async function getChatConversations(
  {
    page,
    pageSize,
    chatId,
    userId,
    originSurface,
  }: {
    page: number;
    pageSize?: number;
    chatId?: string;
    userId?: string;
    originSurface?: ChatOriginSurface;
  },
  options?: GetChatConversationsOptions,
): Promise<GetChatConversationsResponse> {
  const queryString = qs.stringify({
    page,
    pageSize,
    chatId,
    userId,
    originSurface,
  });

  return getRequest(window.configs.API_HOST + `/api/trips/admin/chat-trips?${queryString}`, options);
}

export type ChatTraceResponse = operations['getChatTrace']['responses'][200]['content']['application/json'];
export type ChatTraceExecutionRecord = NonNullable<ChatTraceResponse['result']['turns'][number]['executions']>[number];

export async function getChatMessageSummary(chatId: number, options?: GetChatConversationsOptions) {
  const response: operations['getChatTripsDumpAsAdmin']['responses'][200]['content']['application/json'] =
    await getRequest(`${window.configs.API_HOST}/api/trips/admin/chat-trips/dump?chatId=${chatId}&limit=1`, options);
  const chat = response.result.chats.find((item) => item.id === chatId);
  if (!chat) {
    throw new Error('Chat message summary unavailable');
  }
  const userMessages = chat.messages
    .filter((message) => message.role === 'USER')
    .sort((first, second) => first.messageSeq - second.messageSeq);
  return {
    user: userMessages.length,
    total: chat.messages.length,
    firstUserMessage: userMessages[0]?.content ?? '',
    secondUserMessage: userMessages[1]?.content ?? '',
  };
}
export type ChatTraceEntry = ChatTraceResponse['result']['turns'][number]['entries'][number];
export type ChatTraceAgentStep = Extract<ChatTraceEntry, { itemSeq: number }>;
export type ChatTraceToolExecution = ChatTraceAgentStep['toolExecutions'][number];
export type ChatTraceStoredItem = ChatTraceResponse['result']['unassignedItems'][number];

export async function getChatTrace(
  chatId: string,
  options?: GetChatConversationsOptions & { after?: string },
): Promise<ChatTraceResponse> {
  const query = qs.stringify({ after: options?.after });
  return getRequest(
    `${window.configs.API_HOST}/api/trips/v3/chats/${chatId}/trace${query ? `?${query}` : ''}`,
    options,
  );
}

export type ChatItinerarySnapshot =
  operations['inspectSnapshot']['responses'][200]['content']['application/json']['result']['itinerary'];

export async function getChatItinerarySnapshot(
  chatId: string,
  messageId: string,
  options?: GetChatConversationsOptions,
): Promise<ChatItinerarySnapshot> {
  const response = await getRequest(
    `${tripBasePath()}/v3/chats/${encodeURIComponent(chatId)}/snapshots/${encodeURIComponent(messageId)}/itinerary`,
    options,
  );
  return response.result.itinerary;
}

export type ChatSnapshotMessage = Pick<
  operations['getChatTripsDumpAsAdmin']['responses'][200]['content']['application/json']['result']['chats'][number]['messages'][number],
  'id' | 'role' | 'messageSeq' | 'generationStatus'
>;

export async function getChatSnapshotMessages(
  chatId: string,
  options?: GetChatConversationsOptions,
): Promise<Array<ChatSnapshotMessage>> {
  const response: operations['getChatTripsDumpAsAdmin']['responses'][200]['content']['application/json'] =
    await getRequest(`${tripBasePath()}/admin/chat-trips/dump?${qs.stringify({ chatId, limit: 1 })}`, options);
  return (response.result.chats.find((chat) => String(chat.id) === chatId)?.messages ?? []).map(
    ({ id, role, messageSeq, generationStatus }) => ({ id, role, messageSeq, generationStatus }),
  );
}
